// Shared "linked work orders" timeline — used by Assets, Checklists,
// Vendors, Locations, and Inventory/Parts detail views to show every
// work order/task connected to that record. Extracted once the same
// card + rail + date-filter pattern started showing up in a third
// place, to keep them from drifting apart.
import { useMemo, useState } from 'react'
import { Box, Chip, CircularProgress, Stack, Typography, alpha } from '@mui/material'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import { getStatusMeta, PRIORITY_META } from '../features/work-orders/dummy'

export interface WorkOrderTimelineItem {
  id: string
  title: string
  code: string | null
  status: string
  priority: string
  isTask?: boolean
  assignee: string | null
  createdAt: string
  finishedAt: string | null
  /** Extra small chip after the priority chip — e.g. a checklist
   *  response's own status, or how many units of a part were used. */
  extraChip?: { label: string; color: string; bg: string } | null
  onClick: () => void
}

const DATE_PRESETS: { label: string; days: number | 'all' }[] = [
  { label: 'All time', days: 'all' },
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '1y', days: 365 },
]

/** Client-side date-range filter over an already-fetched list. `range`
 *  is a day count subtracted from now, or 'all' for no cutoff — same
 *  preset-diffing convention as DetailedOverview.tsx's time-period
 *  filter, just compact (single pill row) for use inside a nested tab. */
export function useDateRangeFilter<T>(items: T[], getDate: (item: T) => string) {
  const [range, setRange] = useState<number | 'all'>('all')
  const filtered = useMemo(() => {
    if (range === 'all') return items
    const cutoff = Date.now() - range * 86_400_000
    return items.filter((item) => new Date(getDate(item)).getTime() >= cutoff)
  }, [items, range, getDate])
  return { range, setRange, filtered }
}

export function DateRangeFilterPills({ range, onChange }: { range: number | 'all'; onChange: (v: number | 'all') => void }) {
  return (
    <Stack direction="row" spacing={0.75} sx={{ mb: 2.5 }} flexWrap="wrap" useFlexGap>
      {DATE_PRESETS.map((p) => {
        const active = range === p.days
        return (
          <Box key={p.label} role="button" onClick={() => onChange(p.days)}
            sx={{
              px: 1.25, py: 0.4, borderRadius: 99,
              fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
              cursor: 'pointer', border: '1px solid',
              borderColor: active ? 'primary.main' : 'divider',
              bgcolor: active ? 'primary.main' : 'transparent',
              color: active ? 'primary.contrastText' : 'text.secondary',
              transition: 'all 150ms ease', userSelect: 'none',
              '&:hover': { borderColor: 'primary.main', color: active ? 'primary.contrastText' : 'primary.main' },
            }}>
            {p.days === 'all' ? p.label : `Last ${p.label}`}
          </Box>
        )
      })}
    </Stack>
  )
}

export function WorkOrderTimelineList({ items, formatDate, loading, emptyMessage }: {
  items: WorkOrderTimelineItem[]
  formatDate: (d: Date | string, includeTime?: boolean) => string
  loading?: boolean
  emptyMessage: string
}) {
  if (loading) {
    return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={22} /></Box>
  }
  if (items.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{emptyMessage}</Typography>
      </Box>
    )
  }
  return (
    <Box>
      {items.map((item, i) => (
        <WorkOrderTimelineCard key={item.id} item={item} isLast={i === items.length - 1} formatDate={formatDate} />
      ))}
    </Box>
  )
}

function WorkOrderTimelineCard({ item, isLast, formatDate }: {
  item: WorkOrderTimelineItem; isLast: boolean; formatDate: (d: Date | string, includeTime?: boolean) => string
}) {
  const st = getStatusMeta(item.status, item.isTask)
  const pr = PRIORITY_META[item.priority] ?? { label: item.priority, color: '#64748B', bg: '#F8FAFC' }

  return (
    <Stack direction="row" spacing={2}>
      {/* Rail — dot + connecting line down to the next card. The line
          is a flex-1 child so it auto-stretches to match the card's
          height (default flex align-items is stretch). */}
      <Box sx={{ width: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
        <Box sx={{
          width: 12, height: 12, borderRadius: '50%', bgcolor: st.color, flexShrink: 0, mt: 1,
          boxShadow: (t) => `0 0 0 3px ${alpha(st.color, t.palette.mode === 'light' ? 0.18 : 0.3)}`,
        }} />
        {!isLast && <Box sx={{ width: 2, flex: 1, bgcolor: 'divider', mt: 0.5, borderRadius: 1 }} />}
      </Box>

      <Box onClick={item.onClick} sx={{ flex: 1, minWidth: 0, pb: isLast ? 0 : 2.5 }}>
        <Box sx={{
          p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider',
          bgcolor: 'background.paper', cursor: 'pointer',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: alpha(pr.color, 0.4),
            transform: 'translateY(-2px)',
            boxShadow: (t) => `0 8px 20px ${alpha(t.palette.common.black, t.palette.mode === 'light' ? 0.08 : 0.3)}`,
          },
        }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                <BuildOutlinedIcon sx={{ fontSize: 15, color: alpha(pr.color, 0.7) }} />
                <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.35 }}>{item.title}</Typography>
                {item.code && (
                  <Typography sx={{ fontSize: 11.5, color: 'text.disabled', fontFamily: 'ui-monospace, "SFMono-Regular", monospace' }}>
                    {item.code}
                  </Typography>
                )}
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                <Chip label={st.label} size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: st.bg, color: st.color }} />
                <Chip label={pr.label} size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: pr.bg, color: pr.color }} />
                {item.extraChip && (
                  <Chip label={item.extraChip.label} size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: item.extraChip.bg, color: item.extraChip.color }} />
                )}
                {item.assignee && <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{item.assignee}</Typography>}
              </Stack>
            </Box>

            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', whiteSpace: 'nowrap' }}>{formatDate(item.createdAt)}</Typography>
              {item.finishedAt && (
                <Typography sx={{ fontSize: 11, color: 'text.disabled', whiteSpace: 'nowrap', mt: 0.25 }}>
                  Finished {formatDate(item.finishedAt)}
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>
      </Box>
    </Stack>
  )
}
